import { useEffect, useState, createContext, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useStorefrontAuth } from './useStorefrontAuth';

const StorefrontContext = createContext({
  settings: null,
  loading: true,
  isLive: false,
  error: null,
  refresh: () => {},
  // loyalty redemption config (for "Or get FREE for X Exp points" hints)
  loyaltyConfig: { enabled: false, point_value: 0, points_per_dollar: 0 },
  // cart
  cart: [],
  cartCount: 0,
  cartTotal: 0,
  shopDomain: '',
  addItem: () => {},
  updateQty: () => {},
  removeItem: () => {},
  clearCart: () => {},
  checkoutUrl: '',
  // animation events — subscribe to fire UI feedback on add
  onItemAdded: () => () => {},
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CART_KEY = 'rad-storefront-cart-v1';

const numericVariantId = (gid) => (gid || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Loads the public storefront settings + manages the persistent local cart.
 *
 * The cart sits in localStorage so navigating between pages (or hard
 * refreshing) doesn't blow away the user's selections. Final checkout
 * still happens on Shopify via a multi-variant cart permalink, so this
 * context never has to handle payments itself.
 */
export const StorefrontProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopDomain, setShopDomain] = useState('');
  const [loyaltyConfig, setLoyaltyConfig] = useState({
    enabled: false, point_value: 0, points_per_dollar: 0,
  });
  const [cart, setCart] = useState(loadCart);
  const listenersRef = useRef(new Set());
  const { token, customer } = useStorefrontAuth();
  const lastSyncedRef = useRef(null);             // serialised cart we last wrote to server
  const saveTimerRef = useRef(null);
  const hydratedFromServerRef = useRef(false);   // becomes true on first successful pull/merge for this session
  const prevCustomerIdRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/storefront/public`);
      setSettings(res.data);
      setError(null);
    } catch (e) {
      setSettings(null);
      setError(e.response?.status === 404 ? 'not-live' : 'fetch-error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Shop domain — needed to build the final checkout URL. Fetched once
  // and cached; failures are silent so the cart still works locally.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/storefront/shop-info`)
      .then((res) => { if (!cancelled && res.data?.shop_domain) setShopDomain(res.data.shop_domain); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Loyalty redemption config — drives the "Or get FREE for X Exp pts"
  // hints next to product prices. Public endpoint, silent on failure.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/storefront/loyalty-config`)
      .then((res) => { if (!cancelled && res.data) setLoyaltyConfig(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Persist cart to localStorage on every mutation
  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* quota — ignore */ }
  }, [cart]);

  // Sign-in / sign-out cart sync
  // -------------------------------------------------------------------------
  // When a customer signs in we merge their guest cart with the server cart
  // (so items added while logged out aren't lost). When they sign out we
  // reset the "hydrated" flag so the next sign-in re-pulls.
  useEffect(() => {
    const customerId = customer?.id || null;
    const previousId = prevCustomerIdRef.current;
    prevCustomerIdRef.current = customerId;

    if (!token || !customerId) {
      // Logged out — stop syncing. Keep the local cart as the guest cart.
      hydratedFromServerRef.current = false;
      lastSyncedRef.current = null;
      return undefined;
    }
    // Already hydrated for this customer? Nothing to do.
    if (hydratedFromServerRef.current && previousId === customerId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        // Merge whatever's currently in the local cart with whatever was
        // saved on the server. The merge endpoint sums per-variant and
        // returns the canonical list.
        const res = await axios.post(`${API}/storefront/cart/merge`,
          { items: cart },
          { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        const merged = res.data?.items || [];
        setCart(merged);
        lastSyncedRef.current = JSON.stringify(merged);
        hydratedFromServerRef.current = true;
      } catch {
        // Server unreachable — stay with the local cart, retry on next change
      }
    })();
    return () => { cancelled = true; };
  }, [token, customer?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push cart changes back to the server (debounced) once hydrated
  useEffect(() => {
    if (!token || !customer?.id || !hydratedFromServerRef.current) return undefined;
    const serialised = JSON.stringify(cart);
    if (serialised === lastSyncedRef.current) return undefined; // no-op
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/storefront/cart`, { items: cart }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        lastSyncedRef.current = serialised;
      } catch {
        // best-effort — local state remains the source of truth
      }
    }, 600);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [cart, token, customer?.id]);

  const emitAdded = useCallback((item) => {
    listenersRef.current.forEach((fn) => { try { fn(item); } catch { /* ignore */ } });
  }, []);

  const onItemAdded = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const addItem = useCallback((item, qty = 1) => {
    // item: { variantId (gid), productHandle, title, price, image, variantTitle, stock }
    // `stock` is the available inventory; null means "untracked / unlimited".
    if (!item?.variantId) return;
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.variantId === item.variantId);
      const cap = item.stock == null ? Infinity : Math.max(0, Number(item.stock) || 0);
      if (idx >= 0) {
        const existing = prev[idx];
        // Honor the latest stock value when re-adding the same item
        const effectiveCap = existing.stock == null && item.stock == null
          ? Infinity
          : Math.max(0, Number(item.stock ?? existing.stock) || 0);
        const nextQty = Math.min(existing.quantity + qty, effectiveCap);
        if (nextQty === existing.quantity) return prev; // already at cap
        const next = [...prev];
        next[idx] = { ...existing, stock: item.stock ?? existing.stock, quantity: nextQty };
        return next;
      }
      const startQty = Math.min(qty, cap);
      if (startQty <= 0) return prev;
      return [...prev, { ...item, quantity: startQty }];
    });
    emitAdded({ ...item, quantity: qty });
  }, [emitAdded]);

  const updateQty = useCallback((variantId, qty) => {
    setCart((prev) => {
      if (qty <= 0) return prev.filter((c) => c.variantId !== variantId);
      return prev.map((c) => {
        if (c.variantId !== variantId) return c;
        const cap = c.stock == null ? Infinity : Math.max(0, Number(c.stock) || 0);
        return { ...c, quantity: Math.min(qty, cap) };
      });
    });
  }, []);

  const removeItem = useCallback((variantId) => {
    setCart((prev) => prev.filter((c) => c.variantId !== variantId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((n, c) => n + (Number(c.quantity) || 0), 0);
  const cartTotal = cart.reduce((sum, c) => sum + Number(c.price || 0) * (Number(c.quantity) || 0), 0);

  // Build a Shopify multi-item cart permalink:
  //   https://{shop}/cart/{v1}:{q1},{v2}:{q2}
  // Falls back to the bare /cart route when shop domain isn't ready yet.
  let checkoutUrl = shopDomain ? `https://${shopDomain}/cart` : 'https://shopsoftpos.myshopify.com/cart';
  if (cart.length > 0) {
    const segments = cart
      .map((c) => `${numericVariantId(c.variantId)}:${c.quantity}`)
      .filter(Boolean)
      .join(',');
    if (segments) checkoutUrl = `${shopDomain ? `https://${shopDomain}` : 'https://shopsoftpos.myshopify.com'}/cart/${segments}?storefront=rad`;
  }

  return (
    <StorefrontContext.Provider value={{
      settings,
      loading,
      isLive: !!settings,
      error,
      refresh,
      cart,
      cartCount,
      cartTotal,
      shopDomain,
      loyaltyConfig,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      checkoutUrl,
      onItemAdded,
    }}>
      {children}
    </StorefrontContext.Provider>
  );
};

export const useStorefront = () => useContext(StorefrontContext);
